import React from 'react';
import { BaseEdge, getSmoothStepPath, EdgeProps } from '@xyflow/react';
import { useTenantView } from './TenantViewContext';

/**
 * Phase 36.5 — AnimatedPulseEdge
 * 
 * Three visual states driven by the sequential pulse engine:
 * 1. ACTIVE   — Currently being traversed. Bright cyan particle, full neon glow.
 * 2. TRAIL    — Previously visited. Subtle emerald glow, fading over time.
 * 3. IDLE     — Default slate, no animation.
 */

export default function AnimatedPulseEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  source,
  target,
  selected,
}: EdgeProps) {
  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 16,
  });

  const { pulseState, activeNodeIds } = useTenantView();

  // Determine this edge's visual state
  const isCurrentlyTraversing = pulseState?.currentEdgeId === id;
  const isInTrail = pulseState?.visitedEdgeIds?.includes(id) ?? false;
  const isLegacyActive = activeNodeIds?.includes(source) || activeNodeIds?.includes(target);
  const isTrail = isInTrail || (!isCurrentlyTraversing && isLegacyActive);

  // Visual configuration per state
  const strokeColor = selected
    ? '#38bdf8'    // Sky-400 — selected state
    : isCurrentlyTraversing
    ? '#22d3ee'    // Cyan-400 — active traversal
    : isTrail
    ? '#34d399'    // Emerald-400 — trail glow
    : (style.stroke as string) || '#475569'; // Slate-600 — idle

  const strokeWidth = selected ? 3.5 : isCurrentlyTraversing ? 3 : isTrail ? 2 : 2;
  const glowFilter = selected
    ? 'drop-shadow(0 0 10px rgba(56,189,248,0.9)) drop-shadow(0 0 20px rgba(56,189,248,0.6))'
    : isCurrentlyTraversing
    ? 'drop-shadow(0 0 8px rgba(34,211,238,0.9)) drop-shadow(0 0 20px rgba(34,211,238,0.5))'
    : isTrail
    ? 'drop-shadow(0 0 4px rgba(52,211,153,0.5))'
    : 'none';

  return (
    <>
      {/* Base path with glow */}
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          stroke: strokeColor,
          strokeWidth,
          filter: glowFilter,
          transition: 'stroke 0.25s ease, stroke-width 0.25s ease, filter 0.3s ease',
        }}
      />

      {/* Animated particle — only when this edge is being traversed */}
      {isCurrentlyTraversing && (
        <>
          {/* Outer glow particle */}
          <circle r="7" fill="none" stroke="#22d3ee" strokeWidth="1.5" opacity="0.5">
            <animateMotion dur="0.7s" repeatCount="indefinite" path={edgePath} />
          </circle>
          {/* Core bright particle */}
          <circle r="4" fill="#22d3ee" filter="drop-shadow(0 0 10px #22d3ee) drop-shadow(0 0 20px rgba(34,211,238,0.8))">
            <animateMotion dur="0.7s" repeatCount="indefinite" path={edgePath} />
          </circle>
        </>
      )}

      {/* Trail particle — subtle, fading, for previously traversed edges */}
      {isTrail && !isCurrentlyTraversing && (
        <circle r="2.5" fill="#34d399" opacity="0.6" filter="drop-shadow(0 0 4px #34d399)">
          <animateMotion dur="3s" repeatCount="indefinite" path={edgePath} />
        </circle>
      )}
    </>
  );
}
